import React from "react";
import VerifyEmail from "../pages/VerifyEmail";
import PendingAccess from "../pages/PendingAccess";
import DisabledAccess from "../pages/DisabledAccess";
import AuthForm from "./AuthForm";
import VerifyEmailPanel from "./VerifyEmailPanel";
import PollWhileUnverified from "./PollWhileUnverified";
import type { AppUser } from "../hooks/useAuthState";
import { auth } from "../firebase";
import { getAccountGate } from "../lib/accountAccess";

type Props = {
  authReady: boolean;
  user: AppUser | null;
  canDebug: boolean;
  children: React.ReactNode;
};

/**
 * Centraliserar auth-guard logiken som tidigare låg inline i App:
 * - not ready → “Startar…”
 * - /verify → VerifyEmail
 * - not signed in → AuthForm
 * - signed in men email ej verifierad → PollWhileUnverified + VerifyEmailPanel
 * - disabled → tydlig avstängningssida
 * - pending/unassigned/saknad status → PendingAccess
 * - annars rendera children
 */
export function AuthGate({ authReady, user, canDebug, children }: Props): JSX.Element | null {
  if (!authReady) {
    if (canDebug) console.log("[Gate] not-ready");
    return <div style={{ padding: 24 }}>Startar…</div>;
  }

  if (typeof window !== "undefined" && window.location.pathname.startsWith("/verify")) {
    return <VerifyEmail />;
  }

  if (!user) {
    if (canDebug) console.log("[Gate] not-signed-in");
    return <AuthForm />;
  }

  if (canDebug) {
    console.log("[Gate] before email-check", {
      email: user?.email,
      emailVerified: (user as any)?.emailVerified,
      role: (user as any)?.role,
      keys: Object.keys(user || {}),
    });
  }

  if (auth.currentUser && !auth.currentUser.emailVerified) {
    if (canDebug) console.log("[Gate] branch = unverified → poll + panel");
    return (
      <>
        <PollWhileUnverified />
        <VerifyEmailPanel />
      </>
    );
  }

  const accountGate = getAccountGate(user.role, user.status);
  if (accountGate === "disabled") {
    if (canDebug) console.log("[Gate] branch = disabled");
    return <DisabledAccess email={user.email} />;
  }

  if (canDebug) console.log("[Gate] branch = pending/unassigned", { role: user.role, status: user.status });
  if (accountGate === "pending") {
    return <PendingAccess />;
  }

  if (canDebug) console.log("[Gate] branch = app");
  return <>{children}</>;
}
